import "./App.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "./assets/css/style.css";
import "./assets/css/responsive.css";
import "react-datepicker/dist/react-datepicker.css";
import "react-international-phone/style.css";

// Swiper css
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";

import Homepage from "./pages/Homepage";
import { Fragment, useEffect } from "react";
import { Routes, Route } from "react-router-dom";
import ProtectedRoute from "./pages/ProtectedRoute";
import ProtectedRouteLandlord from "./pages/ProtectedRouteLandlord";
import ProtectedRouteTenant from "./pages/ProtectedRouteTenant";
import ProtectedRouteAdmin from "./pages/ProtectedRouteAdmin";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { ThemeProvider } from "./hooks/useThemeContext";

//state management context files
import AuthState from "./context/auth/AuthState";
import TenantState from "./context/tenant/TenantState";
import LandLordState from "./context/landlord/LandLordState";
import ApplicationState from "./context/application/ApplicationState";

//common routes
import Home from "./pages/Home";
import ForgotPassword from "./pages/ForgotPassword";
import Notification from "./pages/common/notifications/Notification";

//landlord routes
import LandLordProfile from "./pages/landlord/landlord-profile/LandLordProfile";

//tenant routes
import NotFoundPage from "./pages/NotFoundPage";
import EditLandLord from "./pages/common/editProfile/EditLandLord";
import ProfileSharing from "./pages/tenant/tenant-profile/ProfileSharing";
import AddPropertyNew from "./pages/landlord/property/AddPropertyNew";
import BankingReadMore from "./pages/tenant/finance/BankingReadMore";
import AssignProperty from "./pages/landlord/property/AssignProperty";
import PropertyPaymentList from "./pages/landlord/property/PropertyPaymentList";
import PaymentDetails from "./pages/landlord/PaymentDetails";
import AcceptProperty from "./pages/tenant/payments-emi/AcceptProperty";
import PropertyPayment from "./pages/tenant/payments-emi/PropertyPayment";
import RentalReview from "./components/Tenant/RentalReview";
import RedirectPage from "./pages/RedirectPage";
import SubLandlord from "./pages/landlord/sub-landlord";
import AuthRefresh from "./layouts/AuthRefresh";
import CreatedTenant from "./pages/landlord/created-tenant/CreatedTenant";
import CreatedTenantDocs from "./pages/landlord/created-tenant/CreatedTenantDocs";
import ReportWrapper from "./pages/landlord/ReportWrapper";
import GdprConsent from "./pages/auth/GdprConsent";
import OnboardingMain from "./pages/onboarding/OnboardingMain";
import SkorList from "./pages/tenant-profile/skorList";
import RedirectTenantHome from "./pages/tenant/tenant-home/RedirectTenantHome";
import ProfileRequestNew from "./pages/landlord/profile-request/ProfileRequestNew";
import UserAuth from "./pages/auth/UserAuth";
import CustomLinkAuthCheck from "./pages/auth/CustomLinkAuthCheck";
import TenantRentalStep from "./pages/tenant/tenant-home/TenantRentalStep";
import TenantKelify from "./pages/tenant/TenantKelify";
import AddIdealistaProperty from "./pages/landlord/property/AddIdealistaProperty";
import TenantRentalPage from "./pages/tenant/tenant-home/TenantRentalPage";
import CreditsTable from "./pages/landlord/credits-ledger/CreditsTable";
import TenantChecksOnboarding from "./pages/tenant-checks/TenantChecksOnboarding";
import TenantBankVerificationRequest from "./pages/tenant/TenantBankVerificationRequest";
import Clarity from "@microsoft/clarity";
import socket from "./socket";
import { haveValue } from "./helper/common";
import WorkspaceLayout from "./layouts/WorkspaceLayout";
import SelectUserType from "./pages/auth/SelectUserType";
import GuestLandlord from "./pages/GuestLandlord";
import "react-phone-input-2/lib/style.css";
import ExtKycVerification from "./pages/tenant/ExtKycVerification";
import ExtTinkVerification from "./pages/tenant/ExtTinkVerification";
import WorkspaceDashboard from "./pages/WorkspaceDashboard";
import ScoringPage from "./pages/tempCalculation";
import ReportOverview from "./pages/landlord/ReportOverview";
import HelpCenter from "./pages/HelpCenter";
import LinkedReportOverview from "./pages/landlord/LinkedReportOverview";
import CapacityReportPage from "./pages/landlord/CapacityReportPage";
import AdminTenantView from "./pages/admin/AdminTenantView";
import AdminAuthHandler from "./pages/auth/AdminAuthHandler";
import SkorProtectPayment from "./pages/skor-protect/SkorProtectPayment";
import BeneficiaryInsurancePayment from "./pages/beneficiary-insurance/BeneficiaryInsurancePayment";
import MobileCapturePage from "./pages/mobile-capture";
import EditBasicInfo from "./pages/onboarding/EditBasicInfo";

function App() {
  useEffect(() => {
    const loggedInBy = localStorage.getItem("loggedin_by");
    const usertype = localStorage.getItem("usertype");
    const saveduserid = localStorage.getItem("saveduserid");

    // Conditions for clarity
    if (
      loggedInBy !== "admin" &&
      [
        "app.skortorent.com",
        // "localhost:3000"
      ].includes(process.env.REACT_APP_URL_NAME)
    ) {
      Clarity.init("ty68z7j73x");
      console.log("clarity initialized");
    } else {
      console.log("clarity not initialized");
    }

    if (usertype === "landlord" && haveValue(saveduserid)) {
      socket.emit("join_landlord_room", saveduserid);
    }
  }, []);

  return (
    <AuthState>
      <LandLordState>
        <TenantState>
          <ApplicationState>
            <Fragment>
              <ThemeProvider>
                <ToastContainer />
                <Routes>
                  <Route path="/temp" element={<ScoringPage />} />
                  <Route path="/auth" element={<SelectUserType />} />
                  <Route path="/tenant/auth" element={<UserAuth />} />
                  <Route path="/landlord/auth" element={<UserAuth />} />
                  <Route path="/guarantor/auth" element={<UserAuth />} />
                  <Route
                    path="/oauth/:workspace_slug"
                    element={<CustomLinkAuthCheck />}
                  />
                  <Route path="/consent" element={<GdprConsent />} />
                  <Route
                    path="/tenant-bank-verification"
                    element={<TenantBankVerificationRequest />}
                  />
                  <Route
                    path="/ext-kyc-verification"
                    element={<ExtKycVerification />}
                  />
                  <Route
                    path="/ext-bank-verification"
                    element={<ExtTinkVerification />}
                  />
                  <Route path="/m/capture" element={<MobileCapturePage />} />
                  <Route path="/forget-password" element={<ForgotPassword />} />
                  <Route path="/guest/access" element={<GuestLandlord />} />
                  <Route
                    path="/bank"
                    element={<ProtectedRoute component={<BankingReadMore />} />}
                  />
                  <Route path="" element={<AuthRefresh />}>
                    {/* nested routing here */}

                    <Route
                      path="/tenant"
                      element={
                        <ProtectedRoute
                          component={
                            <ProtectedRouteTenant
                              component={<RedirectTenantHome />}
                            />
                          }
                        />
                      }
                    />

                    <Route
                      path="onboarding"
                      element={
                        <ProtectedRoute
                          component={
                            <ProtectedRouteTenant
                              component={<OnboardingMain />}
                            />
                          }
                        />
                      }
                    />
                    <Route
                      path="guarantor/onboarding"
                      element={
                        <ProtectedRoute
                          component={
                            <ProtectedRouteTenant
                              component={<OnboardingMain />}
                            />
                          }
                        />
                      }
                    />
                    {/* <Route
                      path="/kelify"
                      element={
                        <ProtectedRoute
                          component={
                            <ProtectedRouteTenant
                              component={<TenantKelify />}
                            />
                          }
                        />
                      }
                    /> */}
                    <Route
                      path="user/edit"
                      element={
                        <ProtectedRoute
                          component={
                            <ProtectedRouteTenant
                              component={<OnboardingMain />}
                            />
                          }
                        />
                      }
                    />
                    <Route
                      path="/tenant/rentalStep"
                      element={
                        <ProtectedRoute
                          component={
                            <ProtectedRouteTenant
                              component={<TenantRentalStep />}
                            />
                          }
                        />
                      }
                    />
                    {/* <Route path="guarantorRequest" element={<SkorList />} /> */}

                    <Route path="/" element={<Homepage />}>
                      <Route
                        path=""
                        element={<ProtectedRoute component={<Home />} />}
                      />
                      <Route
                        path="edit-basic-info"
                        element={
                          <ProtectedRoute
                            component={
                              <ProtectedRouteTenant
                                component={<EditBasicInfo />}
                              />
                            }
                          />
                        }
                      />

                      {/* Tenant Routes Start */}

                      <Route
                        path="/tenant/review/rental"
                        element={
                          <ProtectedRoute
                            component={
                              <ProtectedRouteTenant
                                component={<TenantRentalPage />}
                              />
                            }
                          />
                        }
                      />
                      <Route
                        path="home"
                        element={
                          <ProtectedRoute
                            component={
                              <ProtectedRouteTenant component={<Home />} />
                            }
                          />
                        }
                      />
                      <Route
                        path="home1"
                        element={<ProtectedRoute component={<Home />} />}
                      />
                      <Route
                        path="/kelify"
                        element={
                          <ProtectedRoute
                            component={
                              <ProtectedRouteTenant
                                component={<TenantKelify />}
                              />
                            }
                          />
                        }
                      />

                      <Route
                        path="/awarded/property/:assignedPropId"
                        element={
                          <ProtectedRoute
                            component={
                              <ProtectedRouteTenant
                                component={<PropertyPayment />}
                              />
                            }
                          />
                        }
                      />

                      <Route
                        path="/accept/assigned/property/:assignedPropertyId"
                        element={
                          <ProtectedRoute
                            component={
                              <ProtectedRouteTenant
                                component={<AcceptProperty />}
                              />
                            }
                          />
                        }
                      />

                      <Route
                        path="notification"
                        element={
                          <ProtectedRoute component={<Notification />} />
                        }
                      />

                      {/* Tenant Routes Ends */}

                      {/* Landlord Routes Start */}
                      <Route
                        path="workspace-dashboard"
                        element={
                          <ProtectedRoute
                            component={
                              <ProtectedRouteLandlord
                                component={<WorkspaceDashboard />}
                              />
                            }
                          />
                        }
                      />
                      <Route
                        path="help"
                        element={<ProtectedRoute component={<HelpCenter />} />}
                      />
                      <Route
                        path="landlord-profile"
                        element={
                          <ProtectedRoute
                            component={
                              <ProtectedRouteLandlord
                                component={<LandLordProfile />}
                              />
                            }
                          />
                        }
                      />
                      <Route path="/:workspaceId" element={<WorkspaceLayout />}>
                        <Route
                          path=""
                          element={<ProtectedRoute component={<Home />} />}
                        />
                        <Route
                          path="home"
                          element={
                            <ProtectedRoute
                              component={
                                <ProtectedRouteLandlord component={<Home />} />
                              }
                            />
                          }
                        />
                        <Route
                          path="profile-request"
                          element={
                            <ProtectedRoute
                              component={
                                <ProtectedRouteLandlord
                                  component={<ProfileRequestNew />}
                                />
                              }
                            />
                          }
                        />
                        <Route
                          path="profile-property"
                          element={
                            <ProtectedRoute
                              component={
                                <ProtectedRouteLandlord
                                  component={<SkorList />}
                                />
                              }
                            />
                          }
                        />
                        <Route
                          path="created-tenant"
                          element={
                            <ProtectedRoute
                              component={
                                <ProtectedRouteLandlord
                                  component={<CreatedTenant />}
                                />
                              }
                            />
                          }
                        />
                        <Route
                          path="tenant-check/create"
                          element={
                            <ProtectedRoute
                              component={
                                <ProtectedRouteLandlord
                                  component={<TenantChecksOnboarding />}
                                />
                              }
                            />
                          }
                        />
                        <Route
                          path="tenant-check/docs/:id"
                          element={
                            <ProtectedRoute
                              component={
                                <ProtectedRouteLandlord
                                  component={<CreatedTenantDocs />}
                                />
                              }
                            />
                          }
                        />
                        {/* <Route
                        path="landlord-profile"
                        element={
                          <ProtectedRoute
                            component={
                              <ProtectedRouteLandlord
                                component={<LandLordProfile />}
                              />
                            }
                          />
                        }
                      /> */}
                        <Route
                          path="edit-profile/landlord"
                          element={
                            <ProtectedRoute
                              component={
                                <ProtectedRouteLandlord
                                  component={<EditLandLord />}
                                />
                              }
                            />
                          }
                        />

                        <Route
                          path="payment/property/list"
                          element={
                            <ProtectedRoute
                              component={
                                <ProtectedRouteLandlord
                                  component={<PropertyPaymentList />}
                                />
                              }
                            />
                          }
                        />
                        <Route
                          path="sub/landlords"
                          element={
                            <ProtectedRoute
                              component={
                                <ProtectedRouteLandlord
                                  component={<SubLandlord />}
                                />
                              }
                            />
                          }
                        />

                        <Route
                          path="notification"
                          element={
                            <ProtectedRoute component={<Notification />} />
                          }
                        />

                        <Route
                          path="assignProperty/:property_id"
                          element={
                            <ProtectedRoute
                              component={
                                <ProtectedRouteLandlord
                                  component={<AssignProperty />}
                                />
                              }
                            />
                          }
                        />
                        <Route
                          path="payment/details/:assingedPropId"
                          element={
                            <ProtectedRoute component={<PaymentDetails />} />
                          }
                        />
                        <Route
                          path="landlord/add-property"
                          element={<AddPropertyNew />}
                        />

                        <Route
                          path="addidealist"
                          element={<AddIdealistaProperty />}
                        />
                        <Route
                          path="credits-ledger"
                          element={<CreditsTable />}
                        />
                        <Route
                          path="generatedReport/:id"
                          element={
                            <ProtectedRoute component={<ReportWrapper />} />
                          }
                          // element={<ProtectedRoute component={<SkorList />} />}
                        />
                        <Route
                          path="report-overview"
                          element={
                            <ProtectedRoute component={<ReportOverview />} />
                          }
                          // element={<ProtectedRoute component={<SkorList />} />}
                        />
                        <Route
                          path="linked-report-overview"
                          element={
                            <ProtectedRoute
                              component={<LinkedReportOverview />}
                            />
                          }
                        />
                        <Route
                          path="capacity-report"
                          element={
                            <ProtectedRoute
                              component={<CapacityReportPage />}
                            />
                          }
                        />
                      </Route>
                      {/* Landlord Routes Ends */}

                      <Route
                        path="/profile-sharing/:tenantid"
                        element={
                          <ProtectedRoute component={<ProfileSharing />} />
                        }
                      />

                      <Route
                        path="/review/rental/:assignedPropertyId"
                        element={
                          <ProtectedRoute component={<RentalReview />} />
                        }
                      />
                    </Route>
                    <Route path="/redirect" element={<RedirectPage />} />

                    {/* Admin Routes */}
                    <Route
                      path="/admin/tenant/:tenantid"
                      element={
                        <ProtectedRoute
                          component={
                            <ProtectedRouteAdmin
                              component={<AdminTenantView />}
                            />
                          }
                        />
                      }
                    />
                  </Route>
                  <Route path="*" element={<NotFoundPage />} />

                  {/* Admin Auth Handler - for direct login from admin panel */}
                  <Route path="/admin-auth" element={<AdminAuthHandler />} />
                  <Route
                    path="/skor-protect/payment"
                    element={<SkorProtectPayment />}
                  />
                  <Route
                    path="/beneficiary-insurance/pay"
                    element={<BeneficiaryInsurancePayment />}
                  />
                </Routes>
              </ThemeProvider>
            </Fragment>
          </ApplicationState>
        </TenantState>
      </LandLordState>
    </AuthState>
  );
}

export default App;
